import { queryOptions } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  BookingRequest,
  BookingsResponse,
  BookingStatus,
  RawBooking,
  RejectBookingRequest,
  ReviewBookingRequest,
} from '@/api/pending'
import { queryKeys } from '@/api/queryKeys'
import { formatBytes, fmtTime } from '@/lib/format'
import { REJECT_REASON_LABEL } from './review'

function fromRaw(raw: RawBooking): BookingRequest {
  const r = raw.review
  const rejectLabel = r.reject_reason ? (REJECT_REASON_LABEL[r.reject_reason] ?? r.reject_reason) : undefined
  return {
    id: raw.id,
    status: raw.status,
    area: raw.area_name,
    resident: raw.resident.name,
    unit: raw.resident.unit_label ?? '—',
    phone: raw.resident.phone,
    fee: raw.fee_amount,
    guests: raw.guests_count ?? undefined,
    date: raw.starts_at,
    start_time: fmtTime(raw.starts_at),
    end_time: fmtTime(raw.ends_at),
    requested_at: raw.requested_at,
    note: raw.note ?? '',
    reject_reason: rejectLabel && (r.reject_message ? `${rejectLabel} — ${r.reject_message}` : rejectLabel),
    approved_at: raw.status === 'approved' ? (r.reviewed_at ?? undefined) : undefined,
    review: {
      details: !!r.details_checked_at,
      contacted_at: r.contacted_at ?? undefined,
      contact_note: r.contact_note ?? undefined,
      payment_method: r.payment_method ?? undefined,
      proof: r.proof
        ? {
            name: r.proof.file_name,
            size: formatBytes(r.proof.size_bytes),
            kind: r.proof.content_type === 'application/pdf' ? 'pdf' : 'image',
            at: r.proof.uploaded_at,
          }
        : undefined,
    },
  }
}

export const bookingRequestsQuery = (condoId: number, status?: BookingStatus) =>
  queryOptions({
    queryKey: queryKeys.bookings(condoId, status),
    queryFn: ({ signal }) =>
      api
        .get<BookingsResponse>('/condominiums/:id/bookings/requests', {
          params: { id: condoId },
          query: { status },
          signal,
        })
        .then((r) => r.bookings.map(fromRaw)),
  })

export const reviewBooking = (condoId: number, bookingId: number, body: ReviewBookingRequest) =>
  api.patch<RawBooking>('/condominiums/:id/bookings/:bookingId/review', body, {
    params: { id: condoId, bookingId },
  })

export const approveBooking = (condoId: number, bookingId: number) =>
  api.patch<RawBooking>('/condominiums/:id/bookings/:bookingId/approve', undefined, {
    params: { id: condoId, bookingId },
  })

export const rejectBooking = (condoId: number, bookingId: number, body: RejectBookingRequest) =>
  api.patch<RawBooking>('/condominiums/:id/bookings/:bookingId/reject', body, {
    params: { id: condoId, bookingId },
  })
